/**
 * Krok 4: Traffic & Brand
 * traffic_mix + brand_mix + channel_dependency, vliv na Acquisition Quality (7 % Business Quality)
 * M&Ai dark theme
 */
import React, { useState } from 'react';

const TRAFFIC_OPTIONS = [
  {
    value: 'paid_heavy',
    label: 'Převážně placená',
    desc: '70 %+ návštěvnosti přichází z placené reklamy (Meta Ads, Google Ads, Heureka PPC).',
    example: 'Typické pro nové e-shopy nebo agresivně rostoucí byznys.',
    sentiment: 'warning',
  },
  {
    value: 'balanced',
    label: 'Vyvážený mix',
    desc: 'Kombinace placené reklamy, SEO, emailu, sociálních sítí a přímých návštěv.',
    example: 'Zdravý stav: pokud vypnete reklamu, stále máte zákazníky.',
    sentiment: 'good',
  },
  {
    value: 'organic_heavy',
    label: 'Převážně organická',
    desc: '70 %+ návštěv přichází organicky (SEO, přímé návštěvy, emailing, sociální sítě).',
    example: 'Silná pozice: nízké náklady na akvizici, stabilní přísun zákazníků.',
    sentiment: 'great',
  },
  {
    value: 'unknown',
    label: 'Nevím',
    desc: 'Nevím, jak je rozložená moje návštěvnost.',
    example: 'Doporučíme vám to změřit v Google Analytics.',
    sentiment: 'warning',
  },
];

const CHANNEL_OPTIONS = [
  {
    value: 'diversified',
    label: 'Diverzifikovaný',
    desc: 'Vlastní web + více kanálů (SEO, email, marketplace, sociální sítě).',
    example: 'Pokud jeden kanál vypadne, byznys přežije.',
    sentiment: 'great',
  },
  {
    value: 'single_platform',
    label: 'Závislý na jednom kanálu',
    desc: 'Většina tržeb z jedné platformy (pouze Amazon, pouze Meta, pouze marketplace).',
    example: 'Změna algoritmu nebo zákaz účtu = existenční riziko.',
    sentiment: 'warning',
  },
];

const BRAND_OPTIONS = [
  {
    value: 'nonbrand',
    label: 'Non-brand',
    desc: 'Zákazníci nehledají váš e-shop podle názvu. Přicházejí přes generické vyhledávání ("vitamín C", "běžecké boty") nebo reklamu.',
    example: 'Pokud vypnete reklamu, tržby výrazně klesnou. Nemáte vlastní brand awareness.',
    sentiment: 'warning',
  },
  {
    value: 'mixed',
    label: 'Smíšený',
    desc: 'Část zákazníků vás hledá přímo (píšou název e-shopu do Googlu), část přichází přes reklamu nebo generické vyhledávání.',
    example: 'Máte začínající brand, ale stále potřebujete placený marketing pro růst.',
    sentiment: 'good',
  },
  {
    value: 'brand_heavy',
    label: 'Silný brand',
    desc: 'Zákazníci aktivně hledají vaši značku. Píšou název e-shopu přímo do vyhledávače nebo zadávají URL.',
    example: 'Notino, Alza, Gymbeam: zákazníci ví, kam jdou. Nízká závislost na reklamě.',
    sentiment: 'great',
  },
  {
    value: 'unknown',
    label: 'Nevím',
    desc: 'Nevím, jestli mě zákazníci hledají podle značky.',
    example: 'Doporučíme vám to ověřit v Google Search Console.',
    sentiment: 'warning',
  },
];

const sentimentStyles = {
  warning: {
    border: 'rgba(245, 158, 11, 0.3)',
    borderSelected: 'var(--purple-bright)',
    bg: 'rgba(245, 158, 11, 0.05)',
    bgSelected: 'rgba(107, 63, 160, 0.15)',
  },
  good: {
    border: 'rgba(155, 89, 212, 0.2)',
    borderSelected: 'var(--purple-bright)',
    bg: 'var(--purple-dark)',
    bgSelected: 'rgba(107, 63, 160, 0.15)',
  },
  great: {
    border: 'rgba(34, 197, 94, 0.3)',
    borderSelected: 'var(--purple-bright)',
    bg: 'rgba(34, 197, 94, 0.05)',
    bgSelected: 'rgba(107, 63, 160, 0.15)',
  },
};

function OptionCard({ option, selected, onClick }) {
  // Jednotný neutrální styl, žádná signalizace „dobré/špatné" podle sentimentu.
  return (
    <button
      type="button"
      onClick={onClick}
      // Výběr se dosud poznal jen podle barvy rámečku. Čtečka obrazovky
      // tak nedokázala říct, která možnost je zvolená.
      aria-pressed={selected}
      style={{
        padding: '1rem 1.1rem',
        border: `2px solid ${selected ? 'var(--purple-bright)' : 'rgba(155, 89, 212, 0.2)'}`,
        borderRadius: '10px',
        background: selected ? 'rgba(107, 63, 160, 0.15)' : 'var(--purple-dark)',
        cursor: 'pointer',
        textAlign: 'left',
        width: '100%',
        transition: 'all 0.2s',
        boxShadow: selected ? '0 0 20px rgba(155, 89, 212, 0.15)' : 'none',
      }}
    >
      <div style={{ fontWeight: 600, fontSize: '0.95rem', marginBottom: '0.35rem', color: 'var(--star-white)' }}>
        {option.label}
      </div>
      <div style={{ fontSize: '0.84rem', color: 'var(--text-dim)', lineHeight: 1.55 }}>
        {option.desc}
      </div>
    </button>
  );
}

function SectionExplainer({ children }) {
  return (
    <div style={{
      padding: '0.65rem 0.85rem',
      background: 'rgba(107, 63, 160, 0.06)',
      borderLeft: '3px solid rgba(155, 89, 212, 0.3)',
      borderRadius: '0 8px 8px 0',
      marginBottom: '0.85rem',
      fontSize: '0.84rem',
      color: 'var(--text-dim)',
      lineHeight: 1.6,
    }}>
      {children}
    </div>
  );
}

export function Step3_TrafficBrand({ wizardState, setWizardState, onNext, onBack }) {
  const [form, setForm] = useState({ ...wizardState.traffic_brand });
  const [error, setError] = useState(false);

  const handleNext = () => {
    if (!form.traffic_mix || !form.brand_mix || !form.channel_dependency) { setError(true); return; }
    setWizardState(prev => ({ ...prev, traffic_brand: { ...form } }));
    onNext();
  };

  return (
    <div className="step-container">
      <h2>Akvizice zákazníků</h2>
      <p className="step-description">
        Jak získáváte zákazníky a jak silná je vaše značka? Vyberte možnost, která nejlíp sedí na váš e-shop.
      </p>

      <div className="edit-form">

        {/* Traffic Mix */}
        <div style={{ marginBottom: '2rem' }}>
          <div style={{ fontWeight: 600, marginBottom: '0.5rem', fontSize: '1rem', color: 'var(--star-white)' }}>
            Traffic Mix
          </div>
          <SectionExplainer>
            <strong>Odkud přicházejí vaši zákazníci?</strong> Traffic Mix měří poměr mezi placenou a organickou návštěvností.
            Podívejte se do Google Analytics na přehled kanálů (Acquisition → Channels).
          </SectionExplainer>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.75rem' }}>
            {TRAFFIC_OPTIONS.map(opt => (
              <OptionCard
                key={opt.value}
                option={opt}
                selected={form.traffic_mix === opt.value}
                onClick={() => { setForm(prev => ({ ...prev, traffic_mix: opt.value })); setError(false); }}
              />
            ))}
          </div>
        </div>

        {/* Brand Mix */}
        <div style={{ marginBottom: '2rem' }}>
          <div style={{ fontWeight: 600, marginBottom: '0.5rem', fontSize: '1rem', color: 'var(--star-white)' }}>
            Brand Mix
          </div>
          <SectionExplainer>
            <strong>Hledají zákazníci přímo vaši značku?</strong> Brand Mix měří sílu vaší značky, ne odkud zákazníci přijdou.
            Rozdíl oproti Traffic Mix: můžete mít 100 % placenou návštěvnost, ale zákazníci klikají na vaši reklamu, protože znají vaši značku.
            Nebo opačně: máte hodně organického trafficu, ale nikdo nehledá váš název.
            Tip: podívejte se v Google Search Console, kolik vyhledávání obsahuje název vašeho e-shopu.
          </SectionExplainer>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.75rem' }}>
            {BRAND_OPTIONS.map(opt => (
              <OptionCard
                key={opt.value}
                option={opt}
                selected={form.brand_mix === opt.value}
                onClick={() => { setForm(prev => ({ ...prev, brand_mix: opt.value })); setError(false); }}
              />
            ))}
          </div>
        </div>

        {/* Channel Dependency */}
        <div>
          <div style={{ fontWeight: 600, marginBottom: '0.5rem', fontSize: '1rem', color: 'var(--star-white)' }}>
            Závislost na kanálu
          </div>
          <SectionExplainer>
            <strong>Kolik vašich tržeb závisí na jednom kanálu?</strong> Tedy zda většina objednávek jde přes jeden
            marketplace nebo jednu reklamní platformu, nebo je rozložená do více zdrojů.
          </SectionExplainer>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.75rem' }}>
            {CHANNEL_OPTIONS.map(opt => (
              <OptionCard
                key={opt.value}
                option={opt}
                selected={form.channel_dependency === opt.value}
                onClick={() => { setForm(prev => ({ ...prev, channel_dependency: opt.value })); setError(false); }}
              />
            ))}
          </div>
        </div>

      </div>

      {error && <small className="chyba-pole" style={{ display: 'block', marginBottom: '0.5rem' }}>Vyberte prosím jednu možnost v každé ze tří oblastí (u prvních dvou je i „Nevím").</small>}

      <div className="button-group">
        <button onClick={onBack} className="btn-secondary">Zpět</button>
        <button onClick={handleNext} className="btn-primary">Pokračovat</button>
      </div>
    </div>
  );
}
